import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  FormHelperText,
  Switch,
  FormControlLabel,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNotification } from '../../hooks/useNotification';
import { WorkspacesService } from '../../services/WorkspacesService';
import { Environment, UpdateWorkspaceCommand, WorkspacePurpose } from './types';
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
    minWidth: 500,
  },
  requiredLabel: {
    '& .MuiInputLabel-root': {
      '&:after': {
        content: '" *"',
        color: theme.palette.error.main,
      },
    },
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  switchContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
}));

// Utility function for email validation
const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

interface WorkspaceEditProps {
  open: boolean;
  onClose: () => void;
  environment?: Environment;
  workspaceId?: string;
  workspace?: {
    name: string;
    subdomain: string;
    environment: WorkspacePurpose;
    requesterEmail: string;
  };
  onSave: (data: { subdomain: string; environment: WorkspacePurpose; emailPasswordRequired: boolean; customerSuccessEmail: string }) => void;
}

export const WorkspaceEdit: React.FC<WorkspaceEditProps> = ({
  open,
  onClose,
  environment,
  workspaceId,
  workspace,
  onSave,
}) => {
  const classes = useStyles();
  const { showSuccess, showError } = useNotification();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<{
    subdomain: string;
    environment: WorkspacePurpose;
    emailPasswordRequired: boolean;
    customerSuccessEmail: string;
  }>({
    subdomain: workspace?.subdomain || '',
    environment: workspace?.environment || WorkspacePurpose.INTERNAL,
    emailPasswordRequired: false,
    customerSuccessEmail: workspace?.requesterEmail || '',
  });
  const [errors, setErrors] = useState({
    subdomain: '',
    environment: '',
    customerSuccessEmail: '',
  });

  const validateFields = () => {
    const newErrors = {
      subdomain: '',
      environment: '',
      customerSuccessEmail: '',
    };

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    }

    if (!formData.environment.trim()) {
      newErrors.environment = 'Environment is required';
    } else if (!Object.values(WorkspacePurpose).includes(formData.environment as WorkspacePurpose)) {
      newErrors.environment = 'Please select a valid environment';
    }

    if (!formData.customerSuccessEmail.trim()) {
      newErrors.customerSuccessEmail = 'Customer Success email is required';
    } else if (!isValidEmail(formData.customerSuccessEmail)) {
      newErrors.customerSuccessEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return !newErrors.subdomain && !newErrors.environment && !newErrors.customerSuccessEmail;
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      emailPasswordRequired: event.target.checked,
    }));
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    if (!workspaceId) {
      showError('Missing workspace ID');
      return;
    }

    if (!environment) {
      showError('Missing environment information');
      return;
    }

    setSaving(true);

    try {
      const workspacesService = new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi);

      const updateCommand: UpdateWorkspaceCommand = {
        purpose: formData.environment,
        passwordDefinitionEnabled: formData.emailPasswordRequired,
        requesterEmail: formData.customerSuccessEmail,
        subdomain: formData.subdomain,
      };

      await workspacesService.updateWorkspace(environment, workspaceId, updateCommand);

      onSave(formData);
      onClose();
      showSuccess('Workspace updated successfully');
    } catch (error) {
      console.error('Error updating workspace:', error);
      showError('Failed to update workspace. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      subdomain: workspace?.subdomain || '',
      environment: workspace?.environment || WorkspacePurpose.INTERNAL,
      emailPasswordRequired: false,
      customerSuccessEmail: '',
    });
    setErrors({
      subdomain: '',
      environment: '',
      customerSuccessEmail: '',
    });
    onClose();
  };

  // Update form data when workspace prop changes
  React.useEffect(() => {
    if (workspace) {
      setFormData({
        subdomain: workspace.subdomain || '',
        environment: workspace.environment || WorkspacePurpose.INTERNAL,
        emailPasswordRequired: false,
        customerSuccessEmail: workspace.requesterEmail || '',
      });
      // Clear any existing errors when workspace changes
      setErrors({
        subdomain: '',
        environment: '',
        customerSuccessEmail: '',
      });
    }
  }, [workspace]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Typography variant="h6">
          Edit Workspace
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box className={classes.fieldContainer}>
              <TextField
                label="Subdomain *"
                value={formData.subdomain}
                onChange={handleInputChange('subdomain')}
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.subdomain}
                placeholder="Enter subdomain"
                className={classes.requiredLabel}
              />
              {errors.subdomain && (
                <FormHelperText error>
                  {errors.subdomain}
                </FormHelperText>
              )}
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box className={classes.fieldContainer}>
              <FormControl
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.environment}
                className={classes.requiredLabel}
              >
                <InputLabel id="environment-label">Purpose *</InputLabel>
                <Select
                  labelId="environment-label"
                  value={formData.environment}
                  onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value as WorkspacePurpose }))}
                  label="Purpose *"
                >
                  {Object.values(WorkspacePurpose).map((env) => (
                    <MenuItem key={env} value={env}>
                      {env}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.environment && (
                <FormHelperText error>
                  {errors.environment}
                </FormHelperText>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box className={classes.switchContainer}>
          <Divider style={{ marginBottom: 16 }} />

          <Typography variant="subtitle2" style={{ marginBottom: 12 }}>
            Password definition by email only?
          </Typography>

          <Box className={classes.switchRow}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.emailPasswordRequired}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label=""
              style={{ margin: 0, marginRight: 16 }}
            />
            <Typography variant="body2" color="textSecondary">
              {formData.emailPasswordRequired
                ? "Email is required for user registration."
                : "Email is optional for user registration."
              }
            </Typography>
          </Box>

          <FormHelperText style={{ marginTop: 8 }}>
            When disabled, this setting allows user registration without the need to provide an email. Thus, the access manager who registers the user in the environment will be responsible for defining an initial password for the user; just as the user, when choosing to change the password, will have to request it from one of the environment's access managers.
          </FormHelperText>
        </Box>

        <Box className={classes.fieldContainer}>
          <TextField
            label="Requester Email (Customer Success) *"
            value={formData.customerSuccessEmail}
            onChange={handleInputChange('customerSuccessEmail')}
            variant="outlined"
            fullWidth
            size="small"
            error={!!errors.customerSuccessEmail}
            placeholder="Enter Customer Success email"
            type="email"
            className={classes.requiredLabel}
          />
          {errors.customerSuccessEmail && (
            <FormHelperText error>
              {errors.customerSuccessEmail}
            </FormHelperText>
          )}
          <FormHelperText style={{ marginTop: 4 }}>
            Customer Success team email, will receive notifications of provisioned or revoked solutions.
          </FormHelperText>
        </Box>
      </DialogContent>

      <DialogActions style={{ justifyContent: 'flex-end', padding: '16px 24px' }}>
        <Button
          onClick={handleCancel}
          color="default"
          variant="outlined"
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || !workspaceId}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
